import { combineReducers } from 'redux';
import { createSelector } from 'reselect';

import edeSearch from '../components/broker/reducers/brokerReducer';
import accessibility from './accessibility/accessibilityReducer';
import accountActivity from './accountActivity/accountActivityReducer';
import authentication from './authentication/authenticationReducer';
import brokerConnect from './brokerConnect/brokerConnectReducer';
import capApplications from './capApplications/capApplicationsReducer';
import common, * as fromCommon from './common/commonReducer';
import dashboardLite from './dashboardLite/dashboardLiteReducer';
import delegation from './delegation/delegationReducer';
import dmi from './dmi/dmiReducer';
import entity from './entity/assisterReducer';
import featurePlan from './featurePlan/featurePlanReducer';
import individualPortal, * as fromIndividualPortal from './individualPortal/individualPortalReducer';
import memberEnrollment from './memberPortal/enrollmentsReducer';
import memberPortal from './memberPortal/memberPortalReducer';
import qle from './qle/qleReducer';
import ridp from './ridp/ridpReducer';
import sideNav from './sideNav/sideNavReducer';
import ssap, * as fromSsap from './ssap/ssapReducer';
import ticketMgmt from './ticketMgmtPortal/ticketMgmtPortalReducer';
import adminProvision from './admin/adminProvisionReducer';
import adminPortal from './admin/adminPortalReducer';
import manageWorkGroup from './ticketMgmtPortal/manageWorkgroupReducer.ts';

import { EXCHANGE_AND_PROGRAMS_NAMES } from '../constants/ssap/miscellaneous/exchangeAndProgramNames';

export default ({ router }) =>
    combineReducers({
        accessibility,
        authentication,
        brokerConnect,
        common,
        dashboardLite,
        delegation,
        edeSearch,
        entity,
        individualPortal,
        qle,
        router,
        ssap,
        ticketMgmt,
        ridp,
        memberEnrollment,
        dmi,
        memberPortal,
        accountActivity,
        capApplications,
        featurePlan,
        sideNav,
        adminProvision,
        adminPortal,
        manageWorkGroup,
    });

// Router selector

export const getHistory = state => state.router;

// SSAP selectors

export const getSsapState = state => state.ssap;
export const getCachedData = state => fromSsap.getCachedData(state.ssap);
export const getApplicationId = state => fromSsap.getApplicationId(state.ssap);
export const getPreviousApplicationId = state => fromSsap.getPreviousApplicationId(state.ssap);
export const getCaseNumber = state => fromSsap.getCaseNumber(state.ssap);
export const getCoverageYear = state => fromSsap.getCoverageYear(state.ssap);
export const getIfFlowLoaded = state => fromSsap.getIfFlowLoaded(state.ssap);
export const getSsapData = state => fromSsap.getSsapData(state.ssap);
export const getSsapLaunchData = state => fromSsap.getLaunchData(state.ssap);
export const getSsapFlow = state => fromSsap.getSsapFlow(state.ssap);
export const getStructure = state => fromSsap.getStructure(state.ssap);
export const isFormInInitialState = state => fromSsap.isFormInInitialState(state.ssap);
export const selectIfEscUIEnabled = state => fromSsap.selectIfEscUIEnabled(state.ssap);
export const selectIfInlineEligibilityIgnoredForEscPage = state =>
    fromSsap.selectIfInlineEligibilityIgnoredForEscPage(state.ssap);
export const selectIfLivingArrangement = state => fromSsap.selectIfLivingArrangement(state.ssap);
export const selectIfPreviousDataLoaded = state => fromSsap.selectIfPreviousDataLoaded(state.ssap);
export const selectIfOnlyUpdatedFieldsShown = state => fromSsap.selectIfOnlyUpdatedFieldsShown(state.ssap);
export const selectIsItfFeatureEnabled = state => fromSsap.selectIsItfFeatureEnabled(state.ssap);
export const selectIsItuQuestionsDisabled = state => fromSsap.selectIsItuQuestionsDisabled(state.ssap);
export const selectPrimaryTaxFiler = state => fromSsap.selectPrimaryTaxFiler(state.ssap);
export const selectIfShouldInvokeInlineEligibility = state =>
    fromSsap.selectIfShouldInvokeInlineEligibility(state.ssap);
export const selectIsInlineCurrentIncomeEnabled = state => fromSsap.selectIsInlineCurrentIncomeEnabled(state.ssap);
export const selectIsMemberRemovalReasonEnabled = state => fromSsap.selectIsMemberRemovalReasonEnabled(state.ssap);
export const selectSsapPreviousData = state => fromSsap.selectSsapPreviousData(state.ssap);
export const selectBloodRelationships = state => fromSsap.selectBloodRelationships(state.ssap);
export const selectHouseholdMembers = state => fromSsap.selectHouseholdMembers(state.ssap);
export const selectRelations = state => fromSsap.selectRelations(state.ssap);
export const selectIfExtraFinalSummaryShown = state => fromSsap.selectIfExtraFinalSummaryShown(state.ssap);
export const selectIfFinalReviewPageActive = state => fromSsap.selectIfFinalReviewPageActive(state.ssap);
export const selectIfSummaryCrashed = state => fromSsap.selectIfSummaryCrashed(state.ssap);
export const selectIfJumpingRestricted = state => fromSsap.selectIfJumpingRestricted(state.ssap);
export const getApplicantGuids = state => fromSsap.getApplicantGuids(state.ssap);
export const getPreviousApplicantGuids = state => fromSsap.getPreviousApplicantGuids(state.ssap);
export const selectIfShouldFetchEnrollments = state => fromSsap.selectIfShouldFetchEnrollments(state.ssap);
export const selectNonSeekingForCoverageAndEnrolled = state =>
    fromSsap.selectNonSeekingForCoverageAndEnrolled(state.ssap);
export const selectCurrentlyPregnantAndEnrolled = state => fromSsap.selectCurrentlyPregnantAndEnrolled(state.ssap);
export const selectIfPageActiveByPageName = (...args) => fromSsap.selectIfPageActiveByPageName.apply(null, args);
export const selectFirstMatchedPage = createSelector(
    (_, pageName) => pageName,
    getSsapState,
    (pageName, ssapState) => fromSsap.selectFirstMatchedPage(ssapState, pageName),
);
export const selectIfMaxProgressAchieved = state => fromSsap.selectIfMaxProgressAchieved(state.ssap);
export const selectIfRenewalFlow = state => fromSsap.selectIfRenewalFlow(state.ssap);
export const selectIfSsapOpenInReviewMode = state => fromSsap.selectIfSsapOpenInReviewMode(state.ssap);
export const selectDacaEffective = state => fromSsap.selectDacaEffective(state.ssap);
export const selectIfRealtimeCheckEnabled = state => fromSsap.selectIfRealtimeCheckEnabled(state.ssap);
export const selectAtomizedEnrollments = state => fromSsap.selectAtomizedEnrollments(state.ssap);
export const selectBulkEnrollments = state => fromSsap.selectBulkEnrollments(state.ssap);
export const selectAddressVisibilityDueToGeneralDelivery = state =>
    fromSsap.selectAddressVisibilityDueToGeneralDelivery(state.ssap);
export const selectIsDataFetched = state => fromSsap.selectIsDataFetched(state.ssap);
export const selectFormSnapshot = (state, formName) => fromSsap.selectFormSnapshot(state.ssap, formName);
export const selectIfSogiFeatureEnabled = state => fromSsap.selectIfSogiFeatureEnabled(state.ssap);
export const selectSettings = state => fromSsap.selectSettings(state.ssap);
export const selectIfAutoReenrollSopaConsentEnabled = state => fromSsap.selectIfAutoReenrollSopaConsentEnabled(state.ssap);
export const getActivePageName = state => fromSsap.getActivePageName(state.ssap);
export const selectPrimaryContactPerson = state => fromSsap.selectPrimaryContactPerson(state.ssap);
export const selectPrimaryContactPersonHomeAddress = state => fromSsap.selectPrimaryContactPersonHomeAddress(state.ssap);
export const selectIfVoterRegistrationFeatureEnabled = state => fromSsap.selectIfVoterRegistrationFeatureEnabled(state.ssap);
export const selectIfAlwaysDisplayMedicaidQuestionsEnabled = state => fromSsap.selectIfAlwaysDisplayMedicaidQuestionsEnabled(state.ssap);
export const selectFormData = (state, formName) => fromSsap.selectFormData(state.ssap, formName);
export const selectIfMedicaidTerminationFeatureEnabled = state => fromSsap.selectIfMedicaidTerminationFeatureEnabled(state.ssap);
export const selectMedicaidEndDateOverride = state => fromSsap.selectMedicaidEndDateOverride(state.ssap);
export const selectEndDateOfPregnancyOverride = state => fromSsap.selectEndDateOfPregnancyOverride(state.ssap);
export const selectIfSsapInReviewMode = state => fromSsap.selectIfSsapInReviewMode(state.ssap);

// Common selectors

export const getIsForAdminOrOperations = state => fromCommon.getIsForAdminOrOperations(state.common);
export const getIsState = state => fromCommon.getIsState(state.common);
export const getTicketEnabled = state => fromCommon.getTicketEnabled(state.common);
export const getStateCode = state => fromCommon.getStateCode(state.common);
export const isLeaveModalShown = state => fromCommon.isLeaveModalShown(state.common);
export const getLanguage = state => fromCommon.getLanguage(state.common);
export const getServerDate = state => fromCommon.getServerDate(state.common);

// Individual Portal selectors

export const selectMailingAddressFromComPref = state => fromIndividualPortal.selectMailingAddressFromComPref(state.individualPortal);
export const selectIfCommunicationPreferencesLoaded = state => fromIndividualPortal.selectIfCommunicationPreferencesLoaded(state.individualPortal);
export const selectCommunicationPreferences = state => fromIndividualPortal.selectCommunicationPreferences(state.individualPortal);

export const getSideNavDetails = state => state.sideNav;
export const getUnreadMessageCountForSideNav = state => state.sideNav.unreadMessageCount;
// Combined selectors
export const selectExchangeAndProgramNames = createSelector(
    getLanguage,
    getStateCode,
    (language, stateCode) => {
        return EXCHANGE_AND_PROGRAMS_NAMES[stateCode]?.[language] ?? {};
    }
);

